import { z } from 'zod';
import { uuid } from './common.js';

export const ARCHIVE_RETENTION_DAYS = 30;

// ── Read: developers + their clusters (GET /platform/developers) ──
export const platformClusterSummary = z.object({
  id: uuid,
  name: z.string(),
  slug: z.string(),
  archived_at: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postal_code: z.string().nullable(),
  contact_phone: z.string().nullable(),
  contact_email: z.string().nullable(),
  addr_street_enabled: z.boolean(),
  addr_block_enabled: z.boolean(),
  property_type: z.enum(['HOUSING', 'APARTMENT']),
});
export const platformDeveloper = z.object({
  id: uuid,
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  archived_at: z.string().nullable(),
  clusters: z.array(platformClusterSummary),
});
export type PlatformDeveloper = z.infer<typeof platformDeveloper>;
export const listPlatformDevelopersResponse = z.object({
  developers: z.array(platformDeveloper),
});
export type ListPlatformDevelopersResponse = z.infer<typeof listPlatformDevelopersResponse>;

// ── Create developer (bare org — no cluster, no account) ──
export const createDeveloperRequest = z.object({
  developer_name: z.string().trim().min(1).max(120),
});
export type CreateDeveloperRequest = z.infer<typeof createDeveloperRequest>;
export const createDeveloperResponse = z.object({
  developer: platformDeveloper,
});
export type CreateDeveloperResponse = z.infer<typeof createDeveloperResponse>;

// ── Create cluster (no account) under an existing developer ──
export const createClusterRequest = z.object({
  developer_id: uuid,
  cluster_name: z.string().trim().min(1).max(120),
  address: z.string().trim().max(300).optional(),
  city: z.string().trim().max(120).optional(),
  province: z.string().trim().max(120).optional(),
  postal_code: z.string().trim().max(20).optional(),
  contact_phone: z.string().trim().max(40).optional(),
  contact_email: z.string().trim().email().max(200).optional().or(z.literal('')),
  addr_street_enabled: z.boolean().optional(),
  addr_block_enabled: z.boolean().optional(),
  property_type: z.enum(['HOUSING', 'APARTMENT']).optional(),
});
export type CreateClusterRequest = z.infer<typeof createClusterRequest>;
export const createClusterResponse = z.object({
  cluster: platformClusterSummary,
});
export type CreateClusterResponse = z.infer<typeof createClusterResponse>;

// ── Edit a cluster's contact/record fields (PATCH /platform/clusters/:id) ──
export const updateClusterRequest = z.object({
  // High-impact "More" backdoor fields (optional): rename the cluster's display
  // name and/or reassign it to another developer. Slug is intentionally NOT
  // editable — it stays stable as the internal identifier.
  cluster_name: z.string().trim().min(1).max(120).optional(),
  developer_id: uuid.optional(),
  address: z.string().trim().max(300).optional(),
  city: z.string().trim().max(120).optional(),
  province: z.string().trim().max(120).optional(),
  postal_code: z.string().trim().max(20).optional(),
  contact_phone: z.string().trim().max(40).optional(),
  contact_email: z.string().trim().email().max(200).optional().or(z.literal('')),
  // Per-cluster unit-address scheme (Number is always required, no flag).
  addr_street_enabled: z.boolean().optional(),
  addr_block_enabled: z.boolean().optional(),
  property_type: z.enum(['HOUSING', 'APARTMENT']).optional(),
});
export type UpdateClusterRequest = z.infer<typeof updateClusterRequest>;

// ── Platform dashboard stats (GET /platform/stats) ──
const platformStatsCluster = z.object({
  id: uuid,
  name: z.string(),
  units: z.number(),
  occupied_units: z.number(),
  residents: z.number(),
});
export const platformStats = z.object({
  totals: z.object({
    developers: z.number(),
    developers_archived: z.number(),
    clusters: z.number(),
    units: z.number(),
    occupied_units: z.number(),
    residents: z.number(),
    admin_accounts: z.number(),
    security: z.number(),
    active_invite_codes: z.number(),
  }),
  developers: z.array(
    z.object({
      id: uuid,
      name: z.string(),
      units: z.number(),
      occupied_units: z.number(),
      residents: z.number(),
      clusters: z.array(platformStatsCluster),
    }),
  ),
});
export type PlatformStats = z.infer<typeof platformStats>;
